import {
  normalizeWineColor,
  wineColorRank,
  WINE_COLOR_LABEL,
  WINE_COLOR_ORDER,
  type WineColorCanonical,
} from '../../../utils/wineColor';

/**
 * LA CARTE view model — one shape for BOTH public resolver kinds.
 *
 * The public page must render every live slug with the new design NOW,
 * before the Phase C wine-list → menu migration. The resolver returns:
 *   kind === 'menu'      → { menu: {...}, sections: [{ items: [...] }] }
 *   kind === 'wine-list' → { list: {...}, sections: [{ items: [...] }] }
 * These adapters normalize both into a LaCarteModel. Prices pass through
 * untouched: both DTOs are display-ready today (menu prices are MAJOR units;
 * the wine-list page has always rendered its price field as-is).
 */

// ── Input shapes (structural; only the fields we read) ─────────────────────

interface MenuPayloadIn {
  menu: {
    slug: string;
    name: string;
    description: string | null;
    venueName: string | null;
    venueLogoUrl: string | null;
    defaultCurrency: string | null;
  };
  sections: Array<{
    id: string | null;
    name: string;
    description: string | null;
    items: Array<{
      id: string;
      name: string | null;
      subtitle: string | null;
      basePrice: number | null;
      currency: string | null;
      notes: string | null;
      formats: Array<{ name: string; volumeMl: number | null; price: number | null }>;
      kind?: string;
      wine?: {
        wineDisplay: {
          name: string | null;
          displayVintage: string | null;
          producerName: string | null;
          regionName: string | null;
          color: string | null;
        } | null;
        country?: string | null;
        regionPath?: string[];
        // STRUCTURED-CARD payload (dev API): clean name components + stock band
        nameParts?: {
          specialName: string | null;
          designation: string | null;
          classification: string | null;
          grapes: string[];
        } | null;
        bottlesAvailable?: number | null;
        stockBand?: string | null;
        bottleSize: number | null;
      } | null;
    }>;
  }>;
}

interface WineListPayloadIn {
  list: {
    slug: string;
    name: string;
    description: string | null;
    venueName: string | null;
    venueLogoUrl: string | null;
  };
  sections: Array<{
    items: Array<{
      id: string;
      name: string | null;
      producer: string | null;
      vintage: string | null;
      region: string | null;
      country: string | null;
      regionPath: string[];
      regionDisplay: string | null;
      color: string | null;
      bottleSize: number | null;
      price: number | null;
      formats: Array<{ name: string; volumeMl: number | null; price: number | null }>;
      currency: string | null;
      notes: string | null;
      bottlesAvailable?: number | null;
    }>;
  }>;
}

// ── Output view model ──────────────────────────────────────────────────────

export interface LaCarteFormat {
  /** e.g. "Bottle", "Half bottle · 375 ml", or the curated format name. */
  label: string;
  /** Secondary size hint when the label is a curated name, else null. */
  sub: string | null;
  price: number | null;
}

export interface LaCarteWine {
  id: string;
  producer: string | null;
  name: string;
  /** Display vintage string ("2019", "NV") or null. */
  vintage: string | null;
  /** Numeric vintage for sorting; null for NV/unparseable. */
  vintageYear: number | null;
  /** Compact card line — "France · Meursault" (country · deepest tier). */
  regionLine: string | null;
  /** Full deduped hierarchy, broadest → narrowest, country included. */
  regionParts: string[];
  color: WineColorCanonical | null;
  /** Structured extras — rendered only when present. */
  designation: string | null;
  classification: string | null;
  grapes: string[];
  /** Venue-safe scarcity cue; null = unknown (render nothing). */
  stockBand: 'IN_STOCK' | 'LOW_STOCK' | 'SOLD_OUT' | null;
  note: string | null;
  currency: string | null;
  formats: LaCarteFormat[];
  /** Lowest non-null price across formats, for the card + sorting. */
  minPrice: number | null;
  /** The format whose price the card shows (the one carrying minPrice). */
  primaryFormat: LaCarteFormat | null;
  /** True when other priced formats exist — the card price reads "from". */
  isFromPrice: boolean;
  /** Haystack for search: producer, name, region, country, path. */
  searchText: string;
}

export interface LaCarteOtherItem {
  id: string;
  name: string;
  subtitle: string | null;
  note: string | null;
  price: number | null;
  currency: string | null;
  formats: LaCarteFormat[];
}

export interface LaCarteOtherSection {
  id: string;
  name: string;
  description: string | null;
  items: LaCarteOtherItem[];
}

export interface LaCarteModel {
  slug: string;
  title: string;
  description: string | null;
  venueName: string | null;
  venueLogoUrl: string | null;
  defaultCurrency: string | null;
  wines: LaCarteWine[];
  /** Non-wine menu items, kept in their curated sections (menus only). */
  otherSections: LaCarteOtherSection[];
}

// ── Formatting helpers ─────────────────────────────────────────────────────

/** "375 ml" / "1.5 L" pretty-printer. */
const prettyMl = (ml: number): string => (ml >= 1000 ? `${ml / 1000} L` : `${ml} ml`);

/**
 * Smart bottle-size label per the approved design:
 * 375 → "Half bottle · 375 ml", 500 → "Bottle · 500 ml", 750 → "Bottle",
 * 1500 → "Magnum · 1.5 L", anything else → "Bottle · <n> ml".
 */
export const formatLabel = (volumeMl: number | null | undefined): string => {
  if (volumeMl == null || !Number.isFinite(volumeMl)) return 'Bottle';
  if (volumeMl === 375) return 'Half bottle · 375 ml';
  if (volumeMl === 500) return 'Bottle · 500 ml';
  if (volumeMl === 750) return 'Bottle';
  if (volumeMl === 1500) return 'Magnum · 1.5 L';
  return `Bottle · ${prettyMl(volumeMl)}`;
};

/**
 * Price string: currency code + localized amount. Integers stay whole
 * ("HKD 1,880"); fractional amounts keep 2 decimals. No hardcoded symbol.
 */
export const formatPrice = (
  price: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (price == null || !Number.isFinite(price)) return '—';
  const amount = Number.isInteger(price)
    ? price.toLocaleString('en-US')
    : price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${amount}` : amount;
};

/** ISO-3166 alpha-2 → full country name ("FR" → "France"); pass-through otherwise. */
const expandCountry = (country: string): string => {
  if (!/^[A-Za-z]{2}$/.test(country)) return country;
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(country.toUpperCase());
    return name && name !== country.toUpperCase() ? name : country;
  } catch {
    return country;
  }
};

/**
 * Full deduped region hierarchy — country (full name) first, then every tier
 * broadest → narrowest. Live data repeats segments (regionPath[0] ===
 * regionName, doubled hierarchy nodes, pre-joined regionDisplay strings), so
 * every candidate is split on '·', trimmed, and deduped case-insensitively.
 */
const buildRegionParts = (
  country: string | null | undefined,
  regionPath: string[] | undefined,
  fallbackRegion: string | null | undefined,
): string[] => {
  const segments: string[] = [];
  const push = (value: string | null | undefined) => {
    if (!value) return;
    for (const part of String(value).split('·')) {
      const t = part.trim();
      if (t) segments.push(t);
    }
  };
  if (country) push(expandCountry(country));
  if (regionPath && regionPath.length > 0) regionPath.forEach(seg => push(seg));
  else push(fallbackRegion);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of segments) {
    const key = s.toLowerCase();
    if (!seen.has(key)) { seen.add(key); out.push(s); }
  }
  return out;
};

/**
 * Compact card line from the full hierarchy: two segments or fewer render
 * whole; longer paths compress to "country · deepest tier" (the appellation
 * that actually names the wine) — the detail sheet shows the full path.
 */
const compactRegionLine = (parts: string[]): string | null => {
  if (parts.length === 0) return null;
  if (parts.length <= 2) return parts.join(' · ');
  return `${parts[0]} · ${parts[parts.length - 1]}`;
};

/**
 * Name-proper from the structured components: grapes + specialName (the
 * composition rule guarantees displayName = producer + grapes + region +
 * specialName, so this is the clean inverse). Empty → caller falls back to
 * the composed name + strips.
 */
const nameProperFrom = (
  nameParts: { specialName: string | null; grapes: string[] } | null | undefined,
): string | null => {
  if (!nameParts) return null;
  const s = [...(nameParts.grapes ?? []), nameParts.specialName ?? '']
    .map(x => (x ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return s || null;
};

/** Client-side band for wine-list payloads (same thresholds as the API). */
const bandFromBottles = (bottles: number | null | undefined): LaCarteWine['stockBand'] => {
  if (bottles == null) return null;
  if (bottles <= 0) return 'SOLD_OUT';
  if (bottles <= 3) return 'LOW_STOCK';
  return 'IN_STOCK';
};

const normalizeBand = (band: string | null | undefined): LaCarteWine['stockBand'] =>
  (band === 'IN_STOCK' || band === 'LOW_STOCK' || band === 'SOLD_OUT') ? band : null;

const parseVintageYear = (vintage: string | null | undefined): number | null => {
  if (!vintage) return null;
  const m = /\b(1[89]\d{2}|20\d{2})\b/.exec(vintage);
  return m ? parseInt(m[1], 10) : null;
};

/**
 * Winery honorifics ignored when matching a producer against the front of a
 * wine name — "Weingut Max Ferd. Richter Riesling…" must match producer
 * "Max Ferd. Richter". Kept deliberately conservative: only unambiguous
 * establishment words, never tokens that can be a substantive name part.
 */
const HONORIFICS = new Set([
  'weingut', 'domaine', 'domaines', 'chateau', 'maison',
  'bodega', 'bodegas', 'cantina', 'tenuta', 'azienda',
]);

/** Case/diacritic-folded token ("Château" → "chateau"). */
const foldToken = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const tokenize = (s: string): Array<{ tok: string; end: number }> => {
  const out: Array<{ tok: string; end: number }> = [];
  const re = /[\p{L}\p{N}]+/gu;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(s))) out.push({ tok: foldToken(m[0]), end: m.index + m[0].length });
  return out;
};

/**
 * The producer is the card's eyebrow, yet names on live data usually start
 * with it too ("Shaw + Smith Chardonnay…" under eyebrow SHAW + SMITH). Strip
 * the leading producer from the display name when its significant tokens are
 * a prefix of the name's — case- and diacritic-insensitive, tolerating a
 * leading honorific on either side. If the producer isn't actually a prefix,
 * or stripping would leave nothing, the name is untouched.
 */
const stripLeadingProducer = (name: string, producer: string | null | undefined): string => {
  if (!producer) return name;
  const nameToks = tokenize(name);
  const fullProd = tokenize(producer).map(t => t.tok);
  if (fullProd.length === 0) return name;
  // Shifted variant drops the producer's own honorific — but only trusted
  // when >=2 significant tokens remain, so producer "Château Musar" (shifted
  // to just [musar]) can never eat the front of "Musar Rouge".
  const shifted = [...fullProd];
  while (shifted.length > 1 && HONORIFICS.has(shifted[0])) shifted.shift();
  const candidates = [fullProd];
  if (shifted.length >= 2 && shifted.length !== fullProd.length) candidates.push(shifted);

  // Match from the raw start AND from past any name-side honorific, so both
  // "Domaine Leflaive Puligny…" (producer "Domaine Leflaive", match at 0) and
  // "Weingut Max Ferd. Richter Riesling…" (producer "Max Ferd. Richter",
  // match after the honorific) resolve.
  let skip = 0;
  while (skip < nameToks.length - 1 && HONORIFICS.has(nameToks[skip].tok)) skip += 1;
  const starts = skip > 0 ? [0, skip] : [0];

  for (const start of starts) {
    for (const prodToks of candidates) {
      // at least one significant token must survive the strip
      if (nameToks.length - start <= prodToks.length) continue;
      let match = true;
      for (let k = 0; k < prodToks.length; k += 1) {
        if (nameToks[start + k].tok !== prodToks[k]) { match = false; break; }
      }
      if (!match) continue;
      const rest = name.slice(nameToks[start + prodToks.length - 1].end).replace(/^[\s,·–:-]+/, '').trim();
      return rest || name;
    }
  }
  return name;
};

/**
 * Names on live data often already end in the vintage year
 * ("… Sjaelland 2021"), which doubled with the separate vintage figure.
 * Strip a trailing year from the display name; the year still surfaces as
 * the vintage when the record itself has none. NV names are untouched.
 */
const splitTrailingYear = (rawName: string): { name: string; year: number | null } => {
  const m = /^(.*\S)[\s,·–-]+(1[89]\d{2}|20\d{2})$/.exec(rawName.trim());
  if (!m) return { name: rawName.trim(), year: null };
  return { name: m[1].trim(), year: parseInt(m[2], 10) };
};

/**
 * Display name + single-source vintage. The producer eyebrow and the vintage
 * figure each appear exactly once on the card, so both are stripped from the
 * name when the name already carries them.
 */
const resolveNameVintage = (
  rawName: string,
  displayVintage: string | null | undefined,
  producer: string | null | undefined,
): { name: string; vintage: string | null; vintageYear: number | null } => {
  const { name, year } = splitTrailingYear(stripLeadingProducer(rawName, producer));
  const vintage = displayVintage ?? (year != null ? String(year) : null);
  return { name, vintage, vintageYear: parseVintageYear(vintage) ?? year };
};

/**
 * The card shows ONE price — the lowest — plus the size that price buys.
 * primaryFormat is the format carrying that price (ties → first in menu
 * order); with more than one priced format the price reads "from".
 */
const primaryFormatOf = (
  formats: LaCarteFormat[],
  minPrice: number | null,
): { primaryFormat: LaCarteFormat | null; isFromPrice: boolean } => {
  const priced = formats.filter(f => f.price != null);
  if (minPrice == null || priced.length === 0) {
    return { primaryFormat: formats[0] ?? null, isFromPrice: false };
  }
  const primary = priced.find(f => f.price === minPrice) ?? priced[0];
  return { primaryFormat: primary, isFromPrice: priced.length > 1 };
};

const minNonNull = (values: Array<number | null>): number | null => {
  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  return nums.length ? Math.min(...nums) : null;
};

const searchTextFor = (parts: Array<string | null | undefined>): string =>
  parts.filter(Boolean).join(' ').toLowerCase();

// ── Adapters ───────────────────────────────────────────────────────────────

/**
 * Wine formats: curated format rows first (their names + prices), then the
 * base bottle entry (bottleSize + base price) when a base price exists.
 * Curated names keep the size as a secondary hint to avoid "Bottle · Bottle".
 */
const buildWineFormats = (
  basePrice: number | null,
  bottleSize: number | null,
  formats: Array<{ name: string; volumeMl: number | null; price: number | null }>,
): LaCarteFormat[] => {
  const out: LaCarteFormat[] = [];
  if (basePrice != null) {
    out.push({ label: formatLabel(bottleSize), sub: null, price: basePrice });
  }
  for (const f of formats) {
    if (f.name && f.name.trim()) {
      out.push({
        label: f.name,
        sub: f.volumeMl != null ? prettyMl(f.volumeMl) : null,
        price: f.price ?? null,
      });
    } else {
      out.push({ label: formatLabel(f.volumeMl), sub: null, price: f.price ?? null });
    }
  }
  return out;
};

export const toLaCarteModel = (
  kind: 'menu' | 'wine-list',
  data: unknown,
): LaCarteModel => (kind === 'menu'
  ? fromMenuPayload(data as MenuPayloadIn)
  : fromWineListPayload(data as WineListPayloadIn));

const fromMenuPayload = (p: MenuPayloadIn): LaCarteModel => {
  const wines: LaCarteWine[] = [];
  const otherSections: LaCarteOtherSection[] = [];
  const currencyFallback = p.menu.defaultCurrency ?? null;

  for (const section of p.sections ?? []) {
    const others: LaCarteOtherItem[] = [];
    for (const item of section.items ?? []) {
      const currency = item.currency ?? currencyFallback;
      if (item.kind === 'WINE' && item.wine) {
        const wd = item.wine.wineDisplay;
        const formats = buildWineFormats(
          item.basePrice ?? null,
          item.wine.bottleSize ?? null,
          item.formats ?? [],
        );
        const regionParts = buildRegionParts(item.wine.country, item.wine.regionPath, wd?.regionName);
        // STRUCTURED-CARD — structured name-proper wins; composed name +
        // strips remain the fallback for snapshot-only / component-less wines.
        const proper = nameProperFrom(item.wine.nameParts);
        const nv = proper
          ? { name: proper, vintage: wd?.displayVintage ?? null, vintageYear: parseVintageYear(wd?.displayVintage) }
          : resolveNameVintage(wd?.name ?? item.name ?? 'Wine', wd?.displayVintage, wd?.producerName);
        wines.push({
          id: item.id,
          producer: wd?.producerName ?? null,
          name: nv.name || 'Wine',
          vintage: nv.vintage,
          vintageYear: nv.vintageYear,
          regionLine: compactRegionLine(regionParts),
          regionParts,
          color: normalizeWineColor(wd?.color),
          designation: item.wine.nameParts?.designation ?? null,
          classification: item.wine.nameParts?.classification ?? null,
          grapes: item.wine.nameParts?.grapes ?? [],
          stockBand: normalizeBand(item.wine.stockBand),
          note: item.notes,
          currency,
          formats,
          minPrice: minNonNull(formats.map(f => f.price)),
          ...primaryFormatOf(formats, minNonNull(formats.map(f => f.price))),
          searchText: searchTextFor([
            wd?.producerName, wd?.name, item.name, wd?.regionName,
            item.wine.country, ...(item.wine.regionPath ?? []),
            ...(item.wine.nameParts?.grapes ?? []),
            item.wine.nameParts?.designation, item.wine.nameParts?.classification,
          ]),
        });
      } else {
        others.push({
          id: item.id,
          name: item.name ?? '—',
          subtitle: item.subtitle,
          note: item.notes,
          price: item.basePrice ?? null,
          currency,
          formats: (item.formats ?? []).map(f => ({
            label: f.name || formatLabel(f.volumeMl),
            sub: f.name && f.volumeMl != null ? prettyMl(f.volumeMl) : null,
            price: f.price ?? null,
          })),
        });
      }
    }
    if (others.length > 0) {
      otherSections.push({
        id: section.id ?? `other-${otherSections.length}`,
        name: section.name,
        description: section.description,
        items: others,
      });
    }
  }

  return {
    slug: p.menu.slug,
    title: p.menu.name,
    description: p.menu.description,
    venueName: p.menu.venueName,
    venueLogoUrl: p.menu.venueLogoUrl,
    defaultCurrency: currencyFallback,
    wines,
    otherSections,
  };
};

const fromWineListPayload = (p: WineListPayloadIn): LaCarteModel => {
  const wines: LaCarteWine[] = [];
  for (const section of p.sections ?? []) {
    for (const item of section.items ?? []) {
      const formats = buildWineFormats(item.price ?? null, item.bottleSize ?? null, item.formats ?? []);
      const nv = resolveNameVintage(item.name ?? 'Wine', item.vintage, item.producer);
      const regionParts = buildRegionParts(item.country, item.regionPath, item.regionDisplay ?? item.region);
      wines.push({
        id: item.id,
        producer: item.producer,
        name: nv.name || 'Wine',
        vintage: nv.vintage,
        vintageYear: nv.vintageYear,
        regionLine: compactRegionLine(regionParts),
        regionParts,
        color: normalizeWineColor(item.color),
        // wine-list DTO has no structured components — fallback pipeline only
        designation: null,
        classification: null,
        grapes: [],
        stockBand: bandFromBottles(item.bottlesAvailable),
        note: item.notes,
        currency: item.currency,
        formats,
        minPrice: minNonNull(formats.map(f => f.price)),
        ...primaryFormatOf(formats, minNonNull(formats.map(f => f.price))),
        searchText: searchTextFor([
          item.producer, item.name, item.region, item.regionDisplay,
          item.country, ...(item.regionPath ?? []),
        ]),
      });
    }
  }
  return {
    slug: p.list.slug,
    title: p.list.name,
    description: p.list.description,
    venueName: p.list.venueName,
    venueLogoUrl: p.list.venueLogoUrl,
    defaultCurrency: null,
    wines,
    otherSections: [],
  };
};

// ── Grouping / sorting / searching ─────────────────────────────────────────

export type SortMode = 'color' | 'priceAsc' | 'priceDesc' | 'vintageNew' | 'vintageOld';

export const SORT_CYCLE: Array<{ mode: SortMode; label: string }> = [
  { mode: 'color', label: 'Color ↓' },
  { mode: 'priceAsc', label: 'Price ↑' },
  { mode: 'priceDesc', label: 'Price ↓' },
  { mode: 'vintageNew', label: 'Vintage ↓' },
  { mode: 'vintageOld', label: 'Vintage ↑' },
];

export const searchWines = (wines: LaCarteWine[], query: string): LaCarteWine[] => {
  const q = query.trim().toLowerCase();
  if (!q) return wines;
  return wines.filter(w => w.searchText.includes(q));
};

/** Sommelier-ordered color groups; price ascending within each group. */
export const groupWinesByColor = (
  wines: LaCarteWine[],
): Array<{ color: WineColorCanonical | 'OTHER'; label: string; wines: LaCarteWine[] }> => {
  const byColor = new Map<string, LaCarteWine[]>();
  for (const w of wines) {
    const key = w.color ?? 'OTHER';
    if (!byColor.has(key)) byColor.set(key, []);
    byColor.get(key)!.push(w);
  }
  const groups: Array<{ color: WineColorCanonical | 'OTHER'; label: string; wines: LaCarteWine[] }> = [];
  for (const color of WINE_COLOR_ORDER) {
    const g = byColor.get(color);
    if (!g) continue;
    groups.push({
      color,
      label: WINE_COLOR_LABEL[color],
      wines: [...g].sort((a, b) => (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity)),
    });
  }
  const other = byColor.get('OTHER');
  if (other) {
    groups.push({
      color: 'OTHER',
      label: 'Other',
      wines: [...other].sort((a, b) => (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity)),
    });
  }
  return groups;
};

export const sortWinesFlat = (wines: LaCarteWine[], mode: SortMode): LaCarteWine[] => {
  const arr = [...wines];
  switch (mode) {
    case 'priceAsc':
      return arr.sort((a, b) => (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity));
    case 'priceDesc':
      return arr.sort((a, b) => (b.minPrice ?? -Infinity) - (a.minPrice ?? -Infinity));
    case 'vintageNew':
      return arr.sort((a, b) => (b.vintageYear ?? 0) - (a.vintageYear ?? 0));
    case 'vintageOld':
      return arr.sort((a, b) => (a.vintageYear ?? 9999) - (b.vintageYear ?? 9999));
    default:
      return arr.sort(
        (a, b) => wineColorRank(a.color) - wineColorRank(b.color)
          || (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity),
      );
  }
};

/** CSS token for a color group's dot (theme-aware via the .lc token set). */
export const dotVarFor = (color: WineColorCanonical | 'OTHER' | null): string => {
  switch (color) {
    case 'SPARKLING': return 'var(--dot-sparkling)';
    case 'WHITE': return 'var(--dot-white)';
    case 'ORANGE': return 'var(--dot-orange)';
    case 'ROSE': return 'var(--dot-rose)';
    case 'RED': return 'var(--dot-red)';
    case 'SWEET': return 'var(--dot-sweet)';
    case 'FORTIFIED': return 'var(--dot-fortified)';
    default: return 'var(--dot-other)';
  }
};
